import { RefCallback, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type UseIntersectionObserverOptions = IntersectionObserverInit & {
  selectors?: string;
  enabled?: boolean;
};
export type UseIntersectionObserverResult = {
  observe: <T extends Element>(ref: T | null) => void;
  unobserve: <T extends Element>(ref: T | null) => void;
};
export const useIntersectionObserver = (
  callback: IntersectionObserverCallback,
  { root, rootMargin, threshold, selectors, enabled = true }: UseIntersectionObserverOptions = {}
): UseIntersectionObserverResult => {
  const observer = useMemo(
    () => new IntersectionObserver(callback, { root, rootMargin, threshold }),
    // Use toString() for threshold array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callback, root, rootMargin, threshold?.toString()]
  );

  const observe = useCallback(
    <T extends Element>(element: T | null) => {
      if (!element) return;
      observer.observe(element);
    },
    [observer]
  );

  const unobserve = useCallback(
    <T extends Element>(element: T | null) => {
      if (!element) return;
      observer.unobserve(element);
    },
    [observer]
  );

  useEffect(() => {
    if (!selectors || !enabled) return;
    const elements = document.querySelectorAll(selectors);
    elements.forEach((el) => {
      observer.observe(el);
    });
    return () => {
      elements.forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, [observer, selectors, enabled]);

  useEffect(
    () => () => {
      observer.disconnect();
    },
    [observer]
  );

  return useMemo(() => ({ observe, unobserve }), [observe, unobserve]);
};

export type UseTrackVisibilityResult = [
  RefCallback<Element | null>,
  { entry: IntersectionObserverEntry; isVisible: boolean },
];
export const useTrackVisibility = (
  options?: IntersectionObserverInit
): UseTrackVisibilityResult => {
  const [target, setTarget] = useState<Element | null>(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry>({} as IntersectionObserverEntry);
  const ref = useCallback<RefCallback<Element | null>>((element) => {
    setTarget(element);
  }, []);
  const callback: IntersectionObserverCallback = useCallback(
    (entries) => {
      const last = Array.from(entries)
        .filter((e) => e.target === target)
        .reverse()[0];
      setEntry(last);
    },
    [target]
  );
  const observer = useIntersectionObserver(callback, options);

  useEffect(() => {
    if (!target) return;
    observer.observe(target);
    return () => {
      observer.unobserve(target);
    };
  }, [observer, target]);

  return [ref, { isVisible: !!entry.isIntersecting, entry }];
};

export const useElementObserver = (options?: UseIntersectionObserverOptions) => {
  const [entries, setEntries] = useState<IntersectionObserverEntry[]>([]);
  const mapRef = useRef<Map<Element, IntersectionObserverEntry>>(new Map());
  const callback: IntersectionObserverCallback = useCallback((entries) => {
    const map = mapRef.current;
    if (!map) return;
    entries.forEach((entry) => {
      map.set(entry.target, entry);
    });
    setEntries(Array.from(map.values()));
  }, []);
  const observer = useIntersectionObserver(callback, options);
  return { entries, ...observer };
};

export const findEntryOfLargestElement = (
  entries: IntersectionObserverEntry[]
): IntersectionObserverEntry | undefined =>
  entries.reduce((prev, cur) => {
    if (!prev) return cur;
    const prevRect = prev.intersectionRect;
    const curRect = cur.intersectionRect;
    if (prevRect.width * prevRect.height < curRect.width * curRect.height) {
      return cur;
    } else {
      return prev;
    }
  }, entries[0]);

export const useLargestElementObserver = (options?: UseIntersectionObserverOptions) => {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const { entries, ...observer } = useElementObserver(options);
  useEffect(() => {
    const largestEntry = findEntryOfLargestElement(entries);
    if (largestEntry?.isIntersecting) {
      setEntry(largestEntry);
    }
  }, [entries]);
  return { entry, entries, ...observer };
};
